import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Spinner,
  Button,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import toastr from 'toastr';

import * as demsActions from '../../store/dems/actions';
import * as campaignsActions from '../../store/campaigns/actions';

import Modal from '../../components/Common/Modal/Modal';
import Alert from '../../components/Common/Modal/Alert';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/Dems/tableColumns';
import {
  CountRowTablePlacement,
  CreateButton,
  ToggleColumnButton,
  ExportButtonPlacement,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';

const List = () => {
  const { t } = useTranslation();
  const dems = useSelector((state) => state.Dems.dems);
  const loading = useSelector((state) => state.Dems.loading);
  const campaigns = useSelector((state) => state.Campaign.campaigns);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedDem, setSelectedDem] = useState(null);
  const [selectedDemToArchive, setSelectedDemToArchive] = useState(null);
  const [selectedDemToExport, setSelectedDemToExport] = useState(null);
  const [selectedDemToResetStats, setSelectedDemToResetStats] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [archiveAlert, setArchiveAlert] = useState(false);
  const [resetStatsAlert, setResetStatsAlert] = useState(false);
  const [exportHtmlModal, setExportHtmlModal] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(demsActions.syncDems());
    dispatch(campaignsActions.syncCampaigns());
    return () => dispatch(demsActions.resetState());
  }, [dispatch]);

  const openViewModal = useCallback(
    (dem) => {
      setSelectedDem(dem);
      setViewModal(true);
    },
    [setSelectedDem, setViewModal],
  );

  const openEdit = useCallback(
    (row) => {
      history.push(`/dems/edit/${row.id}`);
    },
    [history],
  );

  const openArchiveAlert = useCallback(
    (dem) => {
      setSelectedDemToArchive(dem);
      setArchiveAlert(true);
    },
    [setSelectedDemToArchive, setArchiveAlert],
  );

  const openResetStatsAlert = useCallback(
    (dem) => {
      setSelectedDemToResetStats(dem);
      setResetStatsAlert(true);
    },
    [setSelectedDemToResetStats, setResetStatsAlert],
  );

  const openExportHtmlModal = useCallback(
    (dem) => {
      setSelectedDemToExport(dem);
      setExportHtmlModal(true);
    },
    [setSelectedDemToExport, setExportHtmlModal],
  );

  const closeExportHtmlModal = useCallback(() => {
    setExportHtmlModal(false);
    setSelectedDemToExport(null);
  }, [setExportHtmlModal, setSelectedDemToExport]);

  const handleCopyHtml = useCallback(async () => {
    if (!selectedDemToExport?.html) {
      toastr.error('No HTML available for this DEM', '');
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedDemToExport.html);
      toastr.success('HTML copied to clipboard', '');
    } catch (error) {
      toastr.error('Failed to copy HTML', '');
    }
  }, [selectedDemToExport]);

  const handleDownloadHtml = useCallback(() => {
    if (!selectedDemToExport?.html) {
      toastr.error('No HTML available for this DEM', '');
      return;
    }
    const htmlFileName = `${(selectedDemToExport.name || 'dem')
      .replace(/\s+/g, '-')
      .toLowerCase()}.html`;
    const blob = new Blob([selectedDemToExport.html], {
      type: 'text/html;charset=utf-8',
    });
    saveAs(blob, htmlFileName);
  }, [selectedDemToExport]);

  const handleArchive = useCallback(() => {
    if (selectedDemToArchive) {
      dispatch(demsActions.archiveDem(selectedDemToArchive.id));
    }
    setArchiveAlert(false);
    setSelectedDemToArchive(null);
  }, [dispatch, selectedDemToArchive]);

  const handleResetStats = useCallback(() => {
    if (selectedDemToResetStats) {
      dispatch(demsActions.resetDemStats(selectedDemToResetStats.id));
    }
    setResetStatsAlert(false);
    setSelectedDemToResetStats(null);
  }, [dispatch, selectedDemToResetStats]);

  const columns = useMemo(
    () =>
      createTableColumns(
        openViewModal,
        openEdit,
        openExportHtmlModal,
        openArchiveAlert,
        campaigns,
        openResetStatsAlert,
      ),
    [openViewModal, openEdit, openExportHtmlModal, openArchiveAlert, campaigns, openResetStatsAlert],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t('DEM')} />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end m-auto">
                    <CountRowTablePlacement />
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <CreateButton
                      label="New DEM"
                      disabled={loading}
                      onPress={() => history.push('/dems/new')}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={dems}
                    columns={columns}
                    filters={filters}
                    setFilters={setFilters}
                    sorting={sorting}
                    setSorting={setSorting}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    hiddenColumns={hiddenColumns}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="Dem"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}
          >
            <ShowSingleElement collection="dems" element={selectedDem} />
          </Modal>
          <Alert
            isOpen={archiveAlert}
            title="Do you want to archive this DEM?"
            confirmBtnLabel="Archive"
            onCancel={() => {
              setArchiveAlert(false);
              setSelectedDemToArchive(null);
            }}
            onConfirm={handleArchive}
          >
            Keep attention!
          </Alert>
          <Alert
            isOpen={resetStatsAlert}
            title="Do you want to reset statistics for this DEM?"
            confirmBtnLabel="Reset"
            onCancel={() => {
              setResetStatsAlert(false);
              setSelectedDemToResetStats(null);
            }}
            onConfirm={handleResetStats}
          >
            This will reset clicks and views counters to zero.
          </Alert>
          <Modal
            size="xl"
            title="Export DEM HTML"
            isOpen={exportHtmlModal}
            toggle={closeExportHtmlModal}
            scrollable={false}
          >
            <div className="modal-body">
              {!selectedDemToExport?.html ? (
                <div className="alert alert-warning mb-0">
                  No HTML is available for this DEM.
                </div>
              ) : (
                <>
                  <Row className="mb-3">
                    <Col>
                      <h5 className="mb-1">{selectedDemToExport.name}</h5>
                      <p className="text-muted mb-0">
                        Preview and copy the full HTML, or download it as a
                        file.
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6" className="mb-3 mb-md-0">
                      <label className="font-weight-bold mb-2">
                        HTML source
                      </label>
                      <pre
                        className="border rounded p-3 bg-light mb-0"
                        style={{
                          maxHeight: '60vh',
                          overflow: 'auto',
                          fontSize: '12px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {selectedDemToExport.html}
                      </pre>
                    </Col>
                    <Col md="6">
                      <label className="font-weight-bold mb-2">Preview</label>
                      <iframe
                        title={`preview_${selectedDemToExport.id}`}
                        srcDoc={selectedDemToExport.html}
                        className="w-100 border rounded"
                        style={{ height: '60vh', backgroundColor: '#fff' }}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </div>
            <div className="modal-footer">
              {selectedDemToExport?.html && (
                <>
                  <Button color="secondary" outline onClick={handleCopyHtml}>
                    <i className="bx bx-copy mr-1" />
                    Copy HTML
                  </Button>
                  <Button color="primary" onClick={handleDownloadHtml}>
                    <i className="bx bx-download mr-1" />
                    Download HTML
                  </Button>
                </>
              )}
              <Button color="secondary" onClick={closeExportHtmlModal}>
                Close
              </Button>
            </div>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
